import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import { TableRow, TableCell } from '@mui/material'

import { SettingsIcon } from 'assets/icons'
import ClubsAndCoursesContainer from 'components/layout/ClubsAndCoursesContainer'
import ClubCourseSearchForm, { SEARCH_TYPE } from 'components/form/ClubCourseSearchForm'
import ClubModalForm from 'components/form/ClubModalForm'
import ErrorMessage from 'components/ui/ErrorMessage'
import InfiniteScrollTable from 'components/ui/InfiniteScrollTable'
import LoadingSpinnerGreen from 'components/ui/LoadingSpinnerGreen'
import RowMenu from 'components/ui/RowMenu'
import { ClientRoute } from 'routes/client'
import { selectCourses, saveSearchFilters } from 'store/courses/slice'
import { searchCourses } from 'store/courses/thunks'
import { selectCourse, clearCourse } from 'store/course/slice'
import { deactivateCourse, reactivateCourse, duplicateCourse } from 'store/course/thunks'
import { generateClubCourseSearchPayload } from 'utils/generateClubCourseSearchPayload'

export default function CourseSearch(): JSX.Element {
  const intl = useIntl()
  const dispatch = useDispatch()
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const [currentClub, setCurrentClub] = useState<number | null>(null)
  const [noFiltersAlert, setNoFiltersAlert] = useState<string | null>(null)
  const searchLimit = 20
  const [rowMenu, setRowMenu] = useState<number | null>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const form = useForm<CourseClubSearchForm>()
  const { getValues, setValue, reset } = form
  const { courses, status, searchFilters } = useSelector(selectCourses)
  const { saveStatus } = useSelector(selectCourse)

  useEffect(() => {
    const filtersSaved = Object.keys(searchFilters).length > 0
    if (filtersSaved) {
      reset(searchFilters)
    }
  }, [searchFilters, reset, setValue])

  const submitSearchForm = (data: CourseClubSearchForm) => {
    setNoFiltersAlert(null)

    const payload = generateSearchPayload(data, 0)

    if (atLeastOneFilterGiven(payload)) {
      dispatch(searchCourses(payload))
    } else {
      setNoFiltersAlert(intl.formatMessage({ id: 'strings.selectAtLeastOneFilter' }))
    }
  }

  const generateSearchPayload = (data: CourseClubSearchForm, offset: number): ClubAndCourseSearchPayload => {
    dispatch(saveSearchFilters(data))
    return generateClubCourseSearchPayload(data, searchLimit, offset)
  }

  const atLeastOneFilterGiven = (payload: ClubAndCourseSearchPayload) => {
    const data = { ...payload.data }
    delete data.limit
    delete data.offset
    delete data.activeClubs
    delete data.activeCourses
    delete data.officialCourses
    return Object.keys(data).length > 0
  }

  const loadMoreResults = () => {
    if (status !== 'loading') {
      const payload = generateSearchPayload(getValues(), courses.length)
      dispatch(searchCourses(payload))
    }
  }

  const columns: TableColumn[] = [
    {
      id: 'courseId',
      label: intl.formatMessage({ id: 'strings.courseId' }),
    },
    {
      id: 'course',
      label: intl.formatMessage({ id: 'strings.course' }),
    },
    {
      id: 'club',
      label: intl.formatMessage({ id: 'strings.club' }),
    },
    {
      id: 'country',
      label: intl.formatMessage({ id: 'strings.country' }),
    },
    {
      id: 'state',
      label: intl.formatMessage({ id: 'strings.state' }),
    },
    {
      id: 'city',
      label: intl.formatMessage({ id: 'strings.city' }),
    },
    {
      id: 'status',
      label: intl.formatMessage({ id: 'strings.status' }),
    },
    {
      id: 'active',
      label: intl.formatMessage({ id: 'strings.active' }),
    },
    {
      id: 'versions',
      label: intl.formatMessage({ id: 'strings.versions' }),
    },
    {
      id: 'gps',
      label: intl.formatMessage({ id: 'strings.gps' }),
    },
    {
      id: 'roundCount',
      label: intl.formatMessage({ id: 'strings.amountOfRounds' }),
    },
    {
      id: 'edit',
      label: '',
    },
    {
      id: 'providerMapping',
      label: intl.formatMessage({ id: 'strings.providerMapping' }),
    },
  ]

  const handleRowMenuOpen = (event: any, id: number) => {
    setRowMenu(id)
    setAnchorEl(event.currentTarget)
  }

  const handleRowMenuClose = () => {
    setRowMenu(null)
    setAnchorEl(null)
    dispatch(clearCourse())
  }

  const toggleModal = () => {
    setCurrentClub(null)
    setModalOpen(!modalOpen)
  }

  const editClub = (clubId: number | undefined) => {
    if (clubId) {
      setCurrentClub(clubId)
      setModalOpen(true)
    }
  }

  const submitDuplicateCourse = (id: number) => {
    dispatch(duplicateCourse(id))
  }

  const submitReactivation = (id: number) => {
    dispatch(reactivateCourse(id))
  }

  const submitDeactivation = (id: number) => {
    dispatch(deactivateCourse(id))
  }

  const getCourseStatus = (course: Course) => {
    return course.isOfficial
      ? intl.formatMessage({ id: 'strings.official' })
      : intl.formatMessage({ id: 'strings.unofficial' })
  }

  const isCourseActive = (course: Course) => {
    return course.isActive ? intl.formatMessage({ id: 'strings.yes' }) : intl.formatMessage({ id: 'strings.no' })
  }

  const getCourseGps = (course: Course) => {
    return course.gps ? intl.formatMessage({ id: 'strings.yes' }) : intl.formatMessage({ id: 'strings.no' })
  }

  const getReOrDeactivateElement = (course: Course) => {
    if (!course.isActive) {
      return <div onClick={() => submitReactivation(course.id)}>{intl.formatMessage({ id: 'strings.reactivate' })}</div>
    }
    return <div onClick={() => submitDeactivation(course.id)}>{intl.formatMessage({ id: 'strings.deactivate' })}</div>
  }

  const getClubName = (course: Course) => {
    const isLinkedToTmOrganization = course.club?.tmOrganizationIds && course.club.tmOrganizationIds.length > 0
    return isLinkedToTmOrganization ? (
      <span>
        {course.club?.name} <sup>TM</sup>
      </span>
    ) : (
      course.club?.name
    )
  }

  return (
    <ClubsAndCoursesContainer>
      <>
        <SearchForm>
          <PageTitle>{intl.formatMessage({ id: 'strings.searchForCourses' })}</PageTitle>
          <ClubCourseSearchForm
            form={form}
            handleSearch={submitSearchForm}
            type={SEARCH_TYPE.Course}
            searchStatus={status}
          />
          <ErrorContainer>{noFiltersAlert && <ErrorMessage>{noFiltersAlert}</ErrorMessage>}</ErrorContainer>
        </SearchForm>

        {modalOpen && <ClubModalForm open={modalOpen} toggle={toggleModal} clubId={currentClub} />}

        <SearchResults>
          <InfiniteScrollTable
            columns={columns}
            rowCount={courses.length}
            hasMoreRows={courses.length >= searchLimit && courses.length % searchLimit === 0}
            loadMore={loadMoreResults}
            noMoreRowsText={courses.length > 0 ? intl.formatMessage({ id: 'strings.noMoreResults' }) : ''}
            status={status}
          >
            {courses.map((course, i) => (
              <TableRow hover key={i}>
                <TableCell>{course.id}</TableCell>
                <TableCell>
                  <Link to={ClientRoute.ADMIN_CLUB_COURSE(course.club?.id || 0, course.id)}> {course.name}</Link>
                </TableCell>
                <TableCell>
                  <ClubName to={ClientRoute.ADMIN_CLUB_COURSES(course.club?.id || 0)}>{getClubName(course)}</ClubName>
                </TableCell>
                <TableCell>{course.country?.name}</TableCell>
                <TableCell>{course.state || ''}</TableCell>
                <TableCell>{course.city}</TableCell>
                <TableCell>{getCourseStatus(course)}</TableCell>
                <TableCell>{isCourseActive(course)}</TableCell>
                <TableCell>{course.versionsCount}</TableCell>
                <TableCell>{getCourseGps(course)}</TableCell>
                <TableCell>{course.roundCount}</TableCell>
                <TableCell>
                  <ActionMenuIcon onClick={(e) => handleRowMenuOpen(e, course.id)} />
                  <RowMenu open={course.id === rowMenu} anchorEl={anchorEl} handleClose={handleRowMenuClose}>
                    {saveStatus === 'loading' ? (
                      <LoadingSpinnerGreen />
                    ) : (
                      <>
                        <div onClick={() => editClub(course.club?.id)}>
                          {intl.formatMessage({ id: 'strings.editClub' })}
                        </div>
                        <div onClick={() => submitDuplicateCourse(course.id)}>
                          {intl.formatMessage({ id: 'strings.duplicate' })}
                        </div>
                        {getReOrDeactivateElement(course)}
                        <div className="disabled">{intl.formatMessage({ id: 'strings.remove' })}</div>
                      </>
                    )}
                  </RowMenu>
                </TableCell>
                <TableCell>{course.provider?.abbreviation || '-'}</TableCell>
              </TableRow>
            ))}
          </InfiniteScrollTable>
        </SearchResults>
      </>
    </ClubsAndCoursesContainer>
  )
}

const PageTitle = styled.h1`
  font-style: italic;
  font-weight: 800;
  font-size: 20px;
  line-height: 26px;
`

const SearchForm = styled.div`
  background-color: ${(props) => props.theme.customPalette.light};
  padding: 20px 40px;
  margin-bottom: 10px;
`

const SearchResults = styled(SearchForm)``

const ErrorContainer = styled.div`
  margin-top: 30px;
`

const ActionMenuIcon = styled(SettingsIcon)`
  width: 20px;
  height: 20px;
  cursor: pointer;
`

const ClubName = styled(Link)`
  color: ${(props) => props.theme.palette.primary.main};

  & sup {
    color: #404040;
    font-size: 10px;
    line-height: 12px;
    font-weight: 700;
    vertical-align: top;
  }
`
